import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { Box, Typography, Button } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useErrorBoundary } from 'react-error-boundary';
import { Statuses } from '../../enums/Statuses';
import classes from './SingleVariationTip.module.scss';
import DateHelper from '../../helpers/date.helper';
import NotFound from '../../pages/not-found/NotFound';
import { Interweave } from 'interweave';
import { RouteNames } from '../../enums/RouteNames';
import VariationTipsService from '../../services/variation-tips.service';
import { ContentCopyOutlined as ContentCopyOutlinedIcon } from '@mui/icons-material';
import Ghost from '../../views/ghost/Ghost';
import Notification from '../../views/notification/Notification';
import { NotificationSeverities } from '../../enums/NotificationSeverities';
import { VariationTip } from '../../interfaces/VariationTip';

const DEFAULT_SUCCESS_MESSAGE = 'Successfully copied to clipboard!';

const SingleVariationTip = () => {
  const { id } = useParams();
  const [variationTip, setVariationTip] = useState<VariationTip | null>(null);
  const [status, setStatus] = useState<Statuses>(Statuses.IDLE);
  const { showBoundary } = useErrorBoundary();

  const renderFacts = (facts: string) => {
    const factLines = facts.split('\n').filter(fact => fact.trim());
    
    return (
      <Box className="mt-4">
        <Box>
          {factLines.map((fact, index) => (
            <Typography 
              key={index} 
              className="mb-2"
              variant={index === 0 ? "h5" : "body1"}
            >
              {fact.trim()}
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };
  const [notification, setNotification] = useState<{ isOpen: boolean, severity: NotificationSeverities, message: string }>(
    { isOpen: false, severity: NotificationSeverities.SUCCESS, message: DEFAULT_SUCCESS_MESSAGE }
  );

  const onCopyButtonClickHandler = useCallback(() => {
    const content = `${variationTip?.attributes.title}\n\n${variationTip?.attributes.analysis}${variationTip?.attributes.facts ? '\n\n' + variationTip.attributes.facts : ''}`;
    navigator.clipboard.writeText(content);
    setNotification({ isOpen: true, severity: NotificationSeverities.SUCCESS, message: DEFAULT_SUCCESS_MESSAGE });
  }, [variationTip]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const abortController = new AbortController();
    setStatus(Statuses.PENDING);
    VariationTipsService.getVariationTipById(id)
      .then((variationTip) => {
        setVariationTip(variationTip);
        setStatus(Statuses.SUCCESS);
      })
      .catch((error: Error) => {
        setStatus(Statuses.ERROR);
        showBoundary(error);
      })

    return () => abortController.abort();
  }, [id, showBoundary]);

  if (variationTip === null && status === Statuses.ERROR) {
    return <NotFound />;
  }

  return (
    <>
      <Box className={` container ${classes['article-box']} p-2 p-sm-5 mt-5 mb-3 mb-0`}>
        {status === Statuses.PENDING ? (
          <Ghost className='article' />
        ) : (
          variationTip && (
            <>
              <Box className='d-flex flex-column flex-lg-row justify-content-between pb-4'>
                {variationTip && variationTip.attributes?.tip?.data?.id ? (
                  <Link
                    className={classes.link}
                    to={`..${RouteNames.SINGLE_TIP}/${variationTip.attributes.tip?.data.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>
                      Original Tip
                    </Typography>
                  </Link>
                ) : (
                  <Typography>
                    Original Tip Removed
                  </Typography>
                )}
                <Typography className={classes['original-text']}>
                  Language: {variationTip.attributes.language?.data.attributes.name}
                </Typography>
                <Typography className={classes['original-text']}>
                  {DateHelper.calculateTimeDifferenceFromNow(dayjs(variationTip.attributes.createdAt))}
                </Typography>
              </Box>
              <Typography
                variant='h4'
                fontWeight='bold'
                className='mb-3'
              >
                {variationTip.attributes.title}
              </Typography>
              <Box className='overflow-hidden'>
                <Interweave content={variationTip.attributes.analysis} />
              </Box>
              {variationTip.attributes.facts && renderFacts(variationTip.attributes.facts)}
              <Box className='d-flex flex-column flex-lg-row justify-content-between mt-4 align-items-center'>
                <Typography className={`${classes['original-text']} mt-3`}>
                  Variation author: {variationTip.attributes.user?.data?.attributes?.username}
                </Typography>
                <Box className='d-flex gap-2 mt-3 mt-lg-0 mb-3 mb-lg-0'>
                  <Button
                    startIcon={<ContentCopyOutlinedIcon />}
                    className={classes['enable-button']}
                    variant='contained'
                    onClick={onCopyButtonClickHandler}
                  >
                    Copy Variation
                  </Button>
                </Box>
              </Box>
            </>
          )
        )}
      </Box>
      <Notification
        open={notification.isOpen}
        onClose={() => setNotification((prevNotification) => ({ ...prevNotification, isOpen: false }))}
        message={notification.message}
        severity={notification.severity}
        position={{ vertical: 'top', horizontal: 'right' }}
      />
    </>
  );
}

export default SingleVariationTip;
